import { NorthEast, SouthEast, Straight } from '@mui/icons-material';
import { CircularProgress, Stack, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useEventTrend } from '../../../../hooks/useEventTrend';
import { useRecoilValue } from 'recoil';
import { selectedDateAtom } from '../../../../data-access';
import { EventDataTrendEventItemWithData } from '@demind-inc/core';
import { EventWithCategory } from '../../../DataView/DataEventDashboard';
import { colorGreenBase, colorRedBase } from '../../../DataView/constants';
import './DataEvent.scss';
import { useCalendarTrend } from '../../../../hooks';
import { ViewAllTitle } from '../../types';
import { ViewAllContainer } from '../ViewAllContainer';
import EventFullView from './EventFullView';
import CompareBox from './CompareBox';
import { useTranslation } from 'react-i18next';

interface EventBoxProps {
  title: string;
  children: React.ReactNode;
  isLoading: boolean;
}

const EventBox: FC<EventBoxProps> = ({ title, children, isLoading }) => (
  <div className="data-event__box">
    <Typography variant="subtitle2" className="data-event__event-box-title">
      {title}
    </Typography>
    {isLoading ? (
      <div className="data-event__inner-box data-event__inner-box--loading">
        <CircularProgress size={16} />
      </div>
    ) : (
      children
    )}
  </div>
);

const DataEvent = () => {
  const { t } = useTranslation();
  const [viewAllTitle, setViewAllTitle] = useState<ViewAllTitle | null>(null);
  const selectedDate = useRecoilValue(selectedDateAtom);
  const {
    dataTrendPeriod,
    isLoading,
    totalEvents,
    eventsDiff,
    totalMeetings,
    meetingsDiff,
    focusTime,
    focusTimeDiff,
  } = useCalendarTrend({ date: selectedDate });
  const {
    eventDailyTrend,
    eventWeeklyTrend,
    isLoading: eventLoading,
  } = useEventTrend({ date: selectedDate });

  const label =
    dataTrendPeriod === 'daily' ? t('data.common.vsPrevDay') : t('data.common.vsPrevWeek');

  const stressfulEvent =
    dataTrendPeriod === 'daily'
      ? (eventDailyTrend?.stressfulEvent as EventDataTrendEventItemWithData)
      : (eventWeeklyTrend?.stressfulEvent as EventDataTrendEventItemWithData);

  const drainingEvent =
    dataTrendPeriod === 'daily'
      ? (eventDailyTrend?.energyDrainingEvent as EventDataTrendEventItemWithData)
      : (eventWeeklyTrend?.energyDrainingEvent as EventDataTrendEventItemWithData);

  const boostingEvent =
    dataTrendPeriod === 'daily'
      ? (eventDailyTrend?.energyBoostingEvent as EventDataTrendEventItemWithData)
      : (eventWeeklyTrend?.energyBoostingEvent as EventDataTrendEventItemWithData);

  return (
    <div className="data-event">
      <ViewAllContainer title={viewAllTitle} onClose={() => setViewAllTitle(null)}>
        <EventFullView
          totalEvents={totalEvents}
          eventDiffFormatted={eventsDiff.formatted}
          eventDiffValue={eventsDiff.value}
          totalMeetings={totalMeetings}
          meetingsDiffFormatted={meetingsDiff.formatted}
          meetingsDiffValue={meetingsDiff.value}
          focusTime={focusTime}
          focusTimeDiffFormatted={focusTimeDiff.formatted}
          focusTimeDiffValue={focusTimeDiff.value}
          label={label}
          stressfulEvent={stressfulEvent}
          drainingEvent={drainingEvent}
          boostingEvent={boostingEvent}
        />
      </ViewAllContainer>
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" className="data-event__title">
          {t('data.events.title')}
        </Typography>
        <Typography
          variant="subtitle2"
          className="data-event__view-all"
          onClick={() => setViewAllTitle(t('data.events.title'))}
        >
          {t('data.common.viewAll')}
        </Typography>
      </Stack>
      <CompareBox
        title={t('data.events.totalEvents')}
        value={totalEvents}
        diffFormatted={eventsDiff.formatted}
        diffValue={eventsDiff.value}
        label={label}
        color={eventsDiff.color}
      />
      <Stack direction={'row'} gap={1}>
        <EventBox title={t('data.events.mostEnergyBoostingEvent')} isLoading={isLoading}>
          {!boostingEvent ? (
            <div className="data-event__inner-box">
              <Typography variant="caption" className="data-event__inner-box__no-data">
                {t('data.common.noData')}
              </Typography>
            </div>
          ) : (
            <EventWithCategory
              className="data-event__inner-box"
              event={boostingEvent.event}
              color={boostingEvent.score >= 0 ? colorGreenBase : colorRedBase}
              score={boostingEvent.score}
              hideIcon
            />
          )}
        </EventBox>
        <EventBox title={t('data.events.mostEnergyDrainingEvent')} isLoading={isLoading}>
          {!drainingEvent ? (
            <div className="data-event__inner-box">
              <Typography variant="caption" className="data-event__inner-box__no-data">
                {t('data.common.noData')}
              </Typography>
            </div>
          ) : (
            <EventWithCategory
              className="data-event__inner-box"
              event={drainingEvent.event}
              color={drainingEvent.score >= 0 ? colorGreenBase : colorRedBase}
              score={drainingEvent.score}
              hideIcon
            />
          )}
        </EventBox>
      </Stack>
    </div>
  );
};

export default DataEvent;
